import { useMemo, useState } from 'react'
import { ActionIcon, TextInput } from '@mantine/core'
import { IconInbox, IconSearch, IconX } from '@tabler/icons-react'
import { cn } from '@/utils/cn'
import {
  ACCESS_POLICY_BADGE_LABELS,
  getThreadLabels,
  isArchivedThread,
  THREAD_LABEL_FOLLOW_UP,
  THREAD_LABEL_URGENT,
  type MessageThreadSummary,
  type MiddlewareThreadSummary,
} from '../service'
import {
  formatParticipantList,
  formatThreadTime,
  getOtherParticipants,
  getPractitionerName,
} from '../presentation'
import { ParticipantAvatarGroup } from './ParticipantAvatarGroup'
import {
  FilterControls,
  hasActiveThreadsFilters,
  type ThreadsFilterOptions,
} from './FilterControls'
import { ThreadTabs, threadTabId, type ThreadTab } from './ThreadTabs'
import { CARD_CLASS } from '../tokens'

type ThreadListProps = {
  threads: MiddlewareThreadSummary[]
  selectedThreadId?: string
  currentPractitionerId?: string
  onSelectThread?: (thread: MessageThreadSummary) => void
  threadsFilterOptions?: ThreadsFilterOptions
  onThreadsFilterOptionsChange?: (value: ThreadsFilterOptions) => void
}

/** This view's tab panel — the full Messages page's conversation list. */
const PANEL_ID = 'thread-tabpanel'

function getPreview(item: MiddlewareThreadSummary): string {
  const payload = item.latestMessage?.payload?.[0]
  return payload?.contentString || payload?.contentAttachment?.title || ''
}

export const ThreadList = ({
  threads,
  selectedThreadId,
  currentPractitionerId,
  onSelectThread,
  threadsFilterOptions,
  onThreadsFilterOptionsChange,
}: ThreadListProps) => {
  const [tab, setTab] = useState<ThreadTab>('active')
  const [search, setSearch] = useState('')

  // Search runs over the already-loaded list — subject, the other participant's
  // name, and the latest-message preview. It deliberately does not reach older
  // message bodies; that would need a server-side search.
  const matched = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return threads
    }

    return threads.filter((item) => {
      // Every participant is indexed, not just the first — on a group thread the
      // person being searched for is often not the one shown in the row.
      const participantNames = getOtherParticipants(item, currentPractitionerId).map(
        (participant) => getPractitionerName(participant.practitioner),
      )
      return [item.thread.topic?.text ?? '', ...participantNames, getPreview(item)].some((field) =>
        field.toLowerCase().includes(query),
      )
    })
  }, [threads, search, currentPractitionerId])

  const activeThreads = matched.filter((item) => !isArchivedThread(item.thread))
  const archivedThreads = matched.filter((item) => isArchivedThread(item.thread))
  const visibleThreads = tab === 'active' ? activeThreads : archivedThreads

  // `threads` arrives already filtered, so an empty list under an active filter
  // is "nothing matched", not "nothing exists" — the two need different copy.
  const filtersNarrowTheList =
    !!threadsFilterOptions && hasActiveThreadsFilters(threadsFilterOptions)

  return (
    <div className={cn(['flex h-full min-h-0 flex-col', CARD_CLASS])}>
      <div className="flex shrink-0 items-center gap-2 p-3">
        <TextInput
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Search conversations"
          aria-label="Search conversations"
          leftSection={<IconSearch size={16} className="text-[#9AA0A8]" />}
          rightSection={
            search ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Clear search"
                onClick={() => setSearch('')}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
          rightSectionPointerEvents={search ? 'all' : 'none'}
          radius="md"
          className="flex-1"
          classNames={{ input: 'border-[#E4E7EB] !text-sm text-[#1F2733]' }}
        />
        {threadsFilterOptions && onThreadsFilterOptionsChange && (
          <FilterControls
            variant="popover"
            threadsFilterOptions={threadsFilterOptions}
            onThreadsFilterOptionsChange={onThreadsFilterOptionsChange}
          />
        )}
      </div>

      <ThreadTabs
        tab={tab}
        onTabChange={setTab}
        activeCount={activeThreads.length}
        archivedCount={archivedThreads.length}
        panelId={PANEL_ID}
      />

      <div
        role="tabpanel"
        id={PANEL_ID}
        aria-labelledby={threadTabId(PANEL_ID, tab)}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {visibleThreads.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center text-[#9AA0A8]">
            <IconInbox size={26} />
            <p className="text-sm">
              {search.trim()
                ? 'No conversations match your search.'
                : filtersNarrowTheList
                  ? 'No conversations match your filters.'
                  : tab === 'active'
                    ? 'No active conversations.'
                    : 'No archived conversations.'}
            </p>
          </div>
        ) : (
          visibleThreads.map((item) => {
            const participants = getOtherParticipants(item, currentPractitionerId)
            // The role chip describes one person, so it only makes sense on a
            // 1:1 conversation — on a group it would attribute one member's
            // access policy to everybody.
            const soleParticipant = participants.length === 1 ? participants[0] : undefined
            const labels = getThreadLabels(item.thread)
            const isUrgent = labels.includes(THREAD_LABEL_URGENT)
            const isFollowUp = labels.includes(THREAD_LABEL_FOLLOW_UP)
            const isSelected = item.thread.id === selectedThreadId
            const unreadCount = item.unreadCount ?? 0
            const preview = getPreview(item)
            const time = formatThreadTime(
              item.latestMessage?.sent ?? item.thread.meta?.lastUpdated ?? item.thread.sent,
            )

            return (
              <button
                key={item.thread.id}
                type="button"
                aria-current={isSelected}
                onClick={() =>
                  onSelectThread?.({
                    thread: item.thread,
                    sender: item.sender,
                    recipients: item.recipients,
                    relatedPatient: item.relatedPatient,
                  })
                }
                className={cn([
                  'relative flex w-full items-start gap-3 border-b border-[#ECEEF1] px-4 py-3 text-left transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#E5533D]',
                  isSelected ? 'bg-[#FCF1EE]' : 'hover:bg-[#F6F7F9]',
                ])}
              >
                {isSelected && (
                  <span className="absolute inset-y-0 left-0 w-[3px] bg-[#E5533D]" aria-hidden />
                )}

                <ParticipantAvatarGroup participants={participants} size={40} />

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-bold text-[#1F2733]">
                      {item.thread.topic?.text || 'Untitled thread'}
                    </span>
                    <span className="shrink-0 text-xs whitespace-nowrap text-[#9AA0A8]">
                      {time}
                    </span>
                  </div>

                  <p className="truncate text-xs font-medium text-[#6B7280]">
                    {formatParticipantList(participants)}
                  </p>

                  {preview && <p className="mt-0.5 truncate text-xs text-[#9AA0A8]">{preview}</p>}

                  <div className="mt-1.5 flex items-center gap-1.5">
                    {soleParticipant && (
                      <span className="behuman-v2-mulish rounded bg-[#F1F2F4] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#6B7280] uppercase">
                        {ACCESS_POLICY_BADGE_LABELS[soleParticipant.accessPolicyName]}
                      </span>
                    )}
                    {isUrgent && (
                      <span className="behuman-v2-mulish rounded bg-[#FEE2E2] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#EF4444] uppercase">
                        Urgent
                      </span>
                    )}
                    {isFollowUp && (
                      <span className="behuman-v2-mulish rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#B45309] uppercase">
                        Follow up
                      </span>
                    )}
                    {unreadCount > 0 && (
                      <span className="behuman-v2-mulish ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-[#E5533D] px-1.5 py-0.5 text-[11px] font-bold text-white">
                        <span aria-hidden>{unreadCount}</span>
                        <span className="sr-only">{`${unreadCount} unread messages`}</span>
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
